import type { WorkProduct } from "../types";

export type WorkProductEditorSurface = "ide" | "rich-text";

// Markdown artifacts authored as files in Specific. Their catalog
// content_type is CODE so they use Monaco, while the generated file itself
// remains Markdown.
const MARKDOWN_IDE_VARIABLES = new Set([
    "constitution",
    "specification-document-product",
    "specification-document-feature",
    "specification-document-user-story",
    "acceptance-criteria-user-story",
    "interface-contract-specifications-user-story",
    "security-implementation-user-story",
    "api-interface-contracts-design-product",
    "api-interface-contracts-design-feature",
    "api-interface-contracts-design-user-story",
    "dependency-graph-user-story",
    "dependencies-preexisting-future-modules-user-story",
    "high-level-technical-design-document",
    "feature-high-level-req",
    "architecture-plan-and-design-user-story",
    "data-model-product",
    "data-model-feature",
    "data-model-user-story"
]);

const MARKDOWN_IDE_NAMES = new Set([
    "constitution",
    "sdd project constitution",
    "architecture plan and design product",
    "architecture plan and design feature",
    "architecture plan and design user story",
    "sdd architecture plan design product",
    "sdd architecture plan design feature",
    "sdd architecture plan design userstory",
    "sdd architecture plan design user story",
    "data model product",
    "data model feature",
    "data model user story"
]);

// These SDD work products are generated documents even where older catalog rows
// use content_type=CODE. CODE historically meant "technical artifact" in the
// catalog, while the editor interpreted it as "file in the Git repository".
const GENERATED_DOCUMENT_VARIABLES = new Set([
    "constitution",
    "specification-document-product",
    "specification-document-feature",
    "specification-document-user-story",
    "acceptance-criteria-user-story",
    "software-requirements-specification-product",
    "product-requirements-definition-product-hierarchy",
    "product-requirements-definition",
    "marketing-personas",
    "interface-contract-specifications-user-story",
    "ubiquitous-language-dictionary-product",
    "ubiquitous-language-dictionary",
    "high-level-technical-design-document",
    "feature-high-level-req",
    "architecture-plan-and-design-user-story",
    "api-contracts-design-product",
    "api-contracts-design-feature",
    "api-contracts-design-user-story",
    "api-interface-contracts-design-product",
    "api-interface-contracts-design-feature",
    "api-interface-contracts-design-user-story",
    "data-model-product",
    "data-model-feature",
    "data-model-user-story",
    "dependencies-pre-existing-future-modules-user-story",
    "dependencies-preexisting-future-modules-user-story",
    "security-implementation-product",
    "security-architecture-document",
    "security-implementation-user-story",
    "test-plan",
    "detailed-requirements",
    "dependency-graph-user-story",
    "user-story-to-spec-mapping-user-story",
    "code-documentation"
]);

const GENERATED_DOCUMENT_NAMES = new Set([
    "constitution",
    "sdd project constitution",
    "specification document product",
    "specification document feature",
    "specification document user story",
    "acceptance criteria",
    "software requirements specification",
    "interface contract specifications",
    "api contracts design product",
    "api contracts design feature",
    "api contracts design user story",
    "api interface contracts design product",
    "api interface contracts design feature",
    "api interface contracts design user story",
    "architecture plan and design product",
    "architecture plan and design feature",
    "architecture plan and design user story",
    "data model product",
    "data model feature",
    "data model user story",
    "dependencies on pre existing and future modules",
    "security implementation product",
    "security implementation feature",
    "security implementation user story",
    "dependency graph",
    "code documentation"
]);

const normalize = (value: unknown): string => String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isArchitecturePlanDesignName = (value: unknown): boolean => {
    const normalized = normalize(value);
    if (!normalized) return false;
    const hasArchitecturePlanDesign =
        normalized.includes("architecture") && normalized.includes("plan") && normalized.includes("design");
    const hasHighLevelTechnicalDesign =
        normalized.includes("high")
        && normalized.includes("level")
        && normalized.includes("technical")
        && normalized.includes("design");
    return hasArchitecturePlanDesign || hasHighLevelTechnicalDesign;
};

const isArchitecturePlanDesignVariable = (value: unknown): boolean => {
    const normalized = normalize(value);
    if (!normalized) return false;
    return normalized.includes("architecture plan and design")
        || normalized.includes("high level technical design")
        || normalized.includes("feature high level req");
};

export function isGeneratedDocumentWorkProduct(workProduct: WorkProduct): boolean {
    const outputFormat = normalize(workProduct.output_format);
    const artifactPath = String(workProduct.artifact_path || "").trim().toLowerCase();
    if (outputFormat === "markdown" || artifactPath.endsWith(".md") || artifactPath.endsWith(".mdx")) return true;

    const variable = String(workProduct.variable || "").trim().toLowerCase();
    if (GENERATED_DOCUMENT_VARIABLES.has(variable)) return true;

    return GENERATED_DOCUMENT_NAMES.has(normalize(workProduct.name));
}

export function resolveWorkProductEditorSurface(workProduct: WorkProduct | null): WorkProductEditorSurface | null {
    if (!workProduct) return null;
    const variable = String(workProduct.variable || "").trim().toLowerCase();
    if (MARKDOWN_IDE_VARIABLES.has(variable)) return "ide";
    if (isArchitecturePlanDesignVariable(workProduct.variable)) return "ide";
    if (MARKDOWN_IDE_NAMES.has(normalize(workProduct.name))) return "ide";
    if (isArchitecturePlanDesignName(workProduct.name)) return "ide";
    const normalizedContentType = String(workProduct.content_type || "").trim().toUpperCase();
    if (normalizedContentType !== "CODE") return "rich-text";
    return isGeneratedDocumentWorkProduct(workProduct) ? "rich-text" : "ide";
}
